/**
 * Predefined AI conversation flow for document generation
 * No GPT calls - uses step-based messages
 */

import { computed } from 'vue';
import type { ConversationMessage } from '../types/docGen.types';

const STEP_AI_MESSAGES: Record<number, string> = {
  1: 'Which document category would you like to create? Please select from below.\n\n' +
    'If you have any saved drafts, they will appear under **Recent Drafts** at the top. ' +
    'Click a draft card to resume where you left off.',
  2: `Great! Now let's choose a template.\n\n` +
    `📤 **Upload New Template**\n` +
    `Select the upload type (ZIP / Single / Multiple), choose a folder, then drag & drop or click the upload area to browse files.\n\n` +
    `📂 **Use Existing Template**\n` +
    `Click the "Select Existing Templates" button to browse templates already registered in Doc Manager.\n\n` +
    `You can combine both — upload new files and select existing ones together.`,
  3: 'Extracting variables from the template...',
  4: 'Here are the variables extracted from the template. Please enter values for each field.',
  5: 'To create documents for additional entities, click the "+ Add Form" button.',
  6: 'Review your input and generate the document when ready.',
  7: 'Preview the generated document. You can switch to edit mode if changes are needed.',
  8: 'Edit the document in OnlyOffice. You can save it when done.',
};

export function useConversation() {
  function getAIMessageForStep(step: number): string {
    return STEP_AI_MESSAGES[step] || 'Please proceed to the next step.';
  }

  function createAIMessage(step: number): ConversationMessage {
    return {
      role: 'ai',
      content: getAIMessageForStep(step),
      timestamp: Date.now(),
      stepId: step,
    };
  }

  function createUserMessage(step: number, content: string): ConversationMessage {
    return {
      role: 'user',
      content,
      timestamp: Date.now(),
      stepId: step,
    };
  }

  return {
    getAIMessageForStep,
    createAIMessage,
    createUserMessage,
  };
}
